import { useCallback, useEffect, useMemo, useState } from "react";

import EmptyState from "../components/EmptyState";
import LoadingState from "../components/LoadingState";
import PageTitle from "../components/PageTitle";
import { adminApi, buildQuery } from "../services/adminApi";
import { formatCurrency, getResults } from "../utils/formatters";
import MenuItemForm from "./MenuItemForm";

const emptyDraft = {
  name: "",
  description: "",
  base_price: "",
  category: "",
  is_active: true,
  is_featured: false,
  location_assignments: [],
};

function appendDraftValue(formData, key, value) {
  if (value !== undefined && value !== null) {
    formData.append(key, value);
  }
}

function normalizeLocationAssignments(assignments) {
  return assignments.map((assignment) => ({
    ...assignment,
    price_override: assignment.price_override || null,
  }));
}

function getLocationBadges(item) {
  if (!item.location_items?.length) {
    return ["No locations assigned"];
  }

  return item.location_items.map((locationItem) => locationItem.location_name);
}

export default function MenuItemsPage({ filters }) {
  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [draftImage, setDraftImage] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [itemLocationFilter, setItemLocationFilter] = useState(filters.location || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationsError, setLocationsError] = useState("");
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  const activeLocations = useMemo(() => {
    return locations.filter((location) => location.is_active);
  }, [locations]);

  const loadMenuItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await adminApi(
        `/menu-items/${buildQuery({
          ...filters,
          location: itemLocationFilter,
        })}`,
      );
      setItems(getResults(payload));
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, [filters, itemLocationFilter]);

  const loadFormOptions = useCallback(async () => {
    setCategoriesLoading(true);
    setLocationsLoading(true);
    setCategoriesError("");
    setLocationsError("");

    try {
      const [categoriesPayload, locationsPayload] = await Promise.all([
        adminApi("/categories/"),
        adminApi("/locations/"),
      ]);
      setCategories(getResults(categoriesPayload));
      setLocations(getResults(locationsPayload));
    } catch (requestError) {
      setCategoriesError("Unable to load categories. Try again.");
      setLocationsError("Unable to load locations. Try again.");
      setError(requestError);
    } finally {
      setCategoriesLoading(false);
      setLocationsLoading(false);
    }
  }, []);

  useEffect(() => {
    setItemLocationFilter(filters.location || "");
  }, [filters.location]);

  useEffect(() => {
    loadFormOptions();
  }, [loadFormOptions]);

  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  const updateDraft = (changes) => {
    setDraft((currentDraft) => ({ ...currentDraft, ...changes }));
  };

  const createMenuItem = async () => {
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    setSuccess("");

    const formData = new FormData();

    Object.entries(draft).forEach(([key, value]) => {
      if (key === "location_assignments") {
        appendDraftValue(
          formData,
          key,
          JSON.stringify(normalizeLocationAssignments(value)),
        );
        return;
      }

      appendDraftValue(formData, key, value);
    });

    if (draftImage) {
      formData.append("image", draftImage);
    }

    try {
      await adminApi("/menu-items/", {
        method: "POST",
        body: formData,
      });
      setDraft(emptyDraft);
      setDraftImage(null);
      setSuccess("Menu item created.");
      await loadMenuItems();
    } catch (requestError) {
      setError(requestError);
      setFieldErrors(requestError.fieldErrors || {});
    } finally {
      setSubmitting(false);
    }
  };

  const updateItemImage = async (item, file) => {
    const formData = new FormData();
    setError(null);

    if (file) {
      formData.append("image", file);
    } else {
      formData.append("clear_image", "true");
    }

    try {
      await adminApi(`/menu-items/${item.id}/`, {
        method: "PATCH",
        body: formData,
      });
      setSuccess(file ? "Image updated." : "Image removed.");
      await loadMenuItems();
    } catch (requestError) {
      setError(requestError);
    }
  };

  const deleteOrArchiveItem = async (item) => {
    setError(null);

    try {
      const response = await adminApi(`/menu-items/${item.id}/`, {
        method: "DELETE",
      });
      setSuccess(response?.detail || "Menu item removed.");
      await loadMenuItems();
    } catch (requestError) {
      setError(requestError);
    }
  };

  const noActiveLocations = !locationsLoading && !locationsError && !activeLocations.length;

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Menu" title="Menu Item Management">
        Create items once, then manage location-specific availability and pricing.
      </PageTitle>

      {error ? <div className="alert alert-error">{error.message}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      <section className="grid-2">
        <MenuItemForm
          categories={categories}
          categoriesError={categoriesError}
          categoriesLoading={categoriesLoading}
          draft={draft}
          fieldErrors={fieldErrors}
          locations={locations}
          locationsError={locationsError}
          locationsLoading={locationsLoading}
          onChange={updateDraft}
          onFileChange={setDraftImage}
          onSubmit={createMenuItem}
          submitting={submitting || noActiveLocations}
        />

        <section className="card admin-panel">
          <header className="card-header">
            <h2>Category Setup</h2>
          </header>
          <div className="card-body">
            <p className="muted-text">
              Use Categories to create or activate menu groups before adding food items.
            </p>
            <a className="btn btn-secondary" href="/admin/categories">
              Manage Categories
            </a>
          </div>
        </section>
      </section>

      <section className="card admin-panel">
        <header className="card-header admin-section-header">
          <h2>Items</h2>
          <label className="form-group admin-inline-filter">
            <span className="form-label">Locations</span>
            <select
              value={itemLocationFilter}
              onChange={(event) => setItemLocationFilter(event.target.value)}
            >
              <option value="">All locations</option>
              {activeLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
              <option value="none">No location assigned</option>
            </select>
          </label>
        </header>
        <div className="card-body mini-grid">
          {loading ? <LoadingState /> : null}
          {!loading && !items.length ? (
            <EmptyState text="No menu items match this location filter." />
          ) : null}
          {items.map((item) => {
            const assignedLocations = item.location_items || [];
            const hasAssignedLocations = assignedLocations.length > 0;
            const noAvailableLocations =
              item.is_active &&
              hasAssignedLocations &&
              !assignedLocations.some((locationItem) => locationItem.is_available);

            return (
              <article className="food-card mini-card" key={item.id}>
                {item.image ? (
                  <img alt={item.name} className="admin-menu-thumb" src={item.image} />
                ) : (
                  <div className="admin-menu-thumb admin-menu-thumb--empty">No image</div>
                )}
                <strong>{item.name}</strong>
                <span>
                  {item.category_name} - {formatCurrency(item.base_price)}
                </span>
                <span>
                  {item.is_active ? "Active" : "Inactive"} -{" "}
                  {item.is_featured ? "Featured" : "Standard"}
                </span>
                {noAvailableLocations ? (
                  <span className="badge badge-warning">
                    Active globally, unavailable at every location
                  </span>
                ) : null}
                <div className="badge-list" aria-label="Locations">
                  {getLocationBadges(item).map((label) => (
                    <span className="badge badge-light" key={label}>
                      {label}
                    </span>
                  ))}
                </div>
                <label className="btn btn-sm btn-light">
                  Replace Image
                  <input
                    accept="image/*"
                    className="visually-hidden"
                    type="file"
                    onChange={(event) => {
                      updateItemImage(item, event.target.files?.[0] || null);
                    }}
                  />
                </label>
                {item.image ? (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => updateItemImage(item, null)}
                    type="button"
                  >
                    Remove Image
                  </button>
                ) : null}
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => deleteOrArchiveItem(item)}
                  type="button"
                >
                  {item.is_archived ? "Archived" : "Delete / Archive"}
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}